<template>
  <div class="flex flex-col gap-5">
    <!--
      The two-way choice, as a radio group rather than two buttons: one tab
      stop, arrow keys between the cards, and `aria-checked` saying which is
      live. Picking one is not an action, it is an answer.
    -->
    <DSRadioGroup
      :model-value="mode"
      class="grid-cols-1 gap-2.5 sm:grid-cols-2"
      aria-label="How this record's account is chosen"
      @update:model-value="handleModeChange"
    >
      <DSChoiceCard
        :value="WizardAccountMode.EXISTING"
        :icon="FolderOpen"
        title="Use an existing account"
        description="Search the accounts already in this repository."
        data-refid="Use an existing account"
      />
      <DSChoiceCard
        :value="WizardAccountMode.NEW"
        :icon="FolderPlus"
        title="Create a new account"
        description="Name it and pick its ownership type."
        data-refid="Create a new account"
      />
    </DSRadioGroup>

    <!-- 2a — pick one the repository already has. -->
    <div
      v-if="!isCreating"
      ref="accountCell"
      class="min-w-0"
    >
      <DSFieldLabel
        class="mb-1.5"
        :for="accountInputId"
        :label="idField.label"
        :mark="markFor(origins[idField.name])"
      />

      <AccountPicker
        :id="accountInputId"
        :model-value="accountId"
        :accounts
        :suggestion
        :loading="hasSentence && accountSuggestionStore.isLoading"
        :askable="accountAskable"
        :placeholder="idField.placeholder"
        empty-label="Nothing to pick — create a new account instead."
        @select="handlePick"
        @ask="handleAskAccountSuggestion"
      >
        <!--
          The scope of the list, read from the same source that filters it so
          the sentence cannot drift from the rule.
        -->
        <template #note>
          <template v-if="accountTypes.length">
            {{ typeSentence }} only.
          </template>
          Feed-maintained accounts are excluded — those are owned by the connection.
        </template>
      </AccountPicker>
    </div>

    <!-- 2b — name one, and say what kind of thing owns it. -->
    <template v-else>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-x-5">
        <WizardField
          :form
          :field="nameField"
          :origin="origins[nameField.name]"
        />

        <div class="min-w-0">
          <!--
            The suggestion lives in the dropdown, exactly as it does for the
            account in the other half: pinned, marked, and with its grounds
            beside it. A chip cannot carry the reason, and a reason the user
            cannot read is a claim they cannot check.
          -->
          <WizardField
            :form
            :field="typeFieldWithOptions"
            :origin="origins[typeField.name]"
            :suggestion="typeSuggestion"
            :suggestion-loading="accountTypeSuggestionStore.isLoading"
            :askable="accountTypeAskable"
            @ask="handleAskAccountTypeSuggestion"
          />
        </div>
      </div>

      <!--
        Said before it happens, not after: nothing is written until the records
        are, so backing out of the wizard cannot leave a stray account behind.
      -->
      <DSAlert tone="info">
        <template #icon>
          <Info />
        </template>
        <DSAlertDescription>
          The new account is created alongside the records at save, not before —
          cancelling leaves nothing behind. Type is limited to the {{ accountTypes.length }} ownership
          types: {{ accountTypes.join(', ') }}
        </DSAlertDescription>
      </DSAlert>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import { FolderOpen, FolderPlus, Info } from '@lucide/vue'

import {
  DSAlert,
  DSAlertDescription,
  DSChoiceCard,
  DSFieldLabel,
  DSRadioGroup,
} from '@ds'

import AccountPicker from '@/entities/assets/components/AccountPicker.vue'

import { useAIAccountSuggestionStore } from '@/store/ai/accountSuggestion'
import { useAIAccountTypeSuggestionStore } from '@/store/ai/accountTypeSuggestion'

import WizardField from './WizardField.vue'

import { ACCOUNT_MODE_FIELD } from '../utils/const'
import { useWizardFieldFocus } from '../utils/focus'
import { markFor } from '../utils/helpers'
import { useWizardOptions } from '../utils/options'
import { AccountOption } from '@/entities/assets/utils/types'
import { AssetWizardAccount, AssetWizardInvestor } from '@/store/ai/assetWizard/utils/types'

import {
  WizardAccountMode,
  WizardFieldDef,
  WizardFieldOrigin,
} from '../utils/types'

/*
 * "Where does this sit?" — the one step the field grid cannot express, because
 * it is a choice between two shapes rather than a set of fields.
 *
 * Both halves write the same three values, so nothing downstream has to know
 * which half was used: picking an existing account copies its name and type in
 * beside its id, and creating one leaves the id empty. The summary shows the
 * name either way, and the schema (see withOwnership) decides which of the
 * three is actually required from the mode.
 *
 * The accounts and the types both come from the repository, narrowed to what
 * the transaction this wizard posts will accept — see useWizardOptions.
 *
 * Two assistants answer here, asked only when the user clicks "Ask AI" on the
 * control that carries the answer — see handleAskAccountSuggestion and
 * handleAskAccountTypeSuggestion. They are gated differently because their
 * questions are. AIType.ACCOUNT_SUGGESTION reads the user's own sentence
 * against those same accounts and picks one, or says none fits — with no
 * sentence there is nothing to read, so the picker offers no "Ask AI" row at
 * all. AIType.ACCOUNT_TYPE_SUGGESTION is askable either way: "what kind of
 * thing owns a property?" is answerable from the flow and the repository's own
 * types, and a user who opened this wizard by hand is the one most likely to
 * want it.
 *
 * Neither blocks the step. No accounts, no websocket or no answer and the
 * controls are simply controls.
 */
const { form, fields, origins, transactionType, flow, sentence } = defineProps<{
  form: any
  fields: WizardFieldDef[]
  origins: Record<string, WizardFieldOrigin>
  /** Decides which account types this wizard may hang a record off. */
  transactionType: string
  /** The wizard being filled in, as context for the suggested account. */
  flow: string
  /*
   * What the user typed, if they typed anything. Passed down rather than read
   * off the assistant's store: the store keeps the last sentence it was given,
   * and a wizard opened directly from the Add asset dialog must not have its
   * account suggested from a sentence about some other asset.
   */
  sentence?: string
  /**
   * Declared so Wizard.vue can hand every bespoke step the same bag. The
   * values are already in the form — see `seedOwnerAccount` — and the sparkle
   * on the fields is what says the assistant put them there.
   */
  ownerAccount?: AssetWizardAccount
  /** Declared for the same reason: only private_fund ever carries this, never this step. */
  investors?: AssetWizardInvestor[]
}>()

const { accountTypesFor, accountsOf } = useWizardOptions()


const accountSuggestionStore = useAIAccountSuggestionStore()
const accountTypeSuggestionStore = useAIAccountTypeSuggestionStore()

const fieldByName = (name: string) => fields.find(field => field.name === name)!

const idField = computed(() => fieldByName('ownerAccountId'))
const nameField = computed(() => fieldByName('ownerAccountName'))
const typeField = computed(() => fieldByName('ownerAccountType'))

const accountInputId = useId()

/*
 * The account is the one required field in this wizard that is not a
 * WizardField, so it registers itself: a banner chip naming it would otherwise
 * open this step and leave the user to find the picker.
 */
const accountCell = ref<HTMLElement>()

useWizardFieldFocus(() => idField.value.name, accountCell)

/*
 * Whether the account assistant has anything to read. Only that one: which of
 * the repository's accounts this is cannot be answered from no words at all,
 * while what kind of account to create can — see accountTypeAskable below.
 */
const hasSentence = computed(() => Boolean(sentence?.trim()))

const accounts = computed(() => accountsOf(transactionType))

const suggestion = computed(() =>
  hasSentence.value ? accountSuggestionStore.getSuggestion : undefined,
)

/*
 * The assistant's read of which kind of thing should own this, in the shape the
 * dropdown pins: a type is its own identity, so it is both value and label.
 */
const typeSuggestion = computed(() => {
  const answer = accountTypeSuggestionStore.getSuggestion
  if (!answer) return undefined

  return { value: answer.accountType, reason: answer.reason }
})

const accountTypes = computed(() => accountTypesFor(transactionType))

/** "Trust, company or person accounts" — the filter, read back as a sentence. */
const typeSentence = computed(() => {
  const types = accountTypes.value
  if (types.length === 1) return `${types[0]} accounts`

  return `${types.slice(0, -1).join(', ')} or ${types.at(-1)} accounts`
})

/*
 * The type field's options cannot come from its definition: which types may own
 * a record depends on the transaction this wizard posts, and a definition is
 * static. The step resolves them and hands them over.
 */
const typeFieldWithOptions = computed(() => ({
  ...typeField.value,
  optionsList: accountTypes.value,
}))

/*
 * Read off the form rather than held here: the step is remounted every time the
 * user walks back into it, and a mode kept in component state would forget
 * which half they were filling in.
 */
const mode = form.useSelector((state: any) => state.values[ACCOUNT_MODE_FIELD])
const accountId = form.useSelector((state: any) => state.values.ownerAccountId)

const isCreating = computed(() => mode.value === WizardAccountMode.NEW)

/*
 * Switching modes clears all three fields. The alternative — keeping the name
 * of an account the user has just stopped pointing at — leaves a value nobody
 * chose in a field the schema is about to require.
 */
const handleModeChange = (next: unknown) => {
  form.setFieldValue(ACCOUNT_MODE_FIELD, next)
  form.setFieldValue('ownerAccountId', '')
  form.setFieldValue('ownerAccountName', '')
  form.setFieldValue('ownerAccountType', '')
}

/** Picking copies the account's own name and type in beside its id. */
const handlePick = (account: AccountOption) => {
  form.setFieldValue('ownerAccountId', account.id)
  form.setFieldValue('ownerAccountName', account.name)
  form.setFieldValue('ownerAccountType', account.type)
}

/*
 * Offered only with a sentence to read: "which of your accounts is this?"
 * cannot be answered from no words at all, so a click here would spend a
 * model call to be told nothing fits. Hidden once asked, whatever the answer —
 * the store's own dedupe would otherwise leave a live-looking row that a
 * second click can't actually repeat.
 */
const accountAskable = computed(
  () => hasSentence.value && !accountSuggestionStore.hasAsked,
)

/*
 * Offered only once the create-new half is on screen — this question only
 * exists for someone who has decided not to pick, so offering it up front
 * would invite a model call for every user who never creates an account.
 *
 * Askable with or without a sentence, unlike the account above it. "Which of
 * your accounts is this?" cannot be answered from nothing, but "what kind of
 * thing owns a property?" can — the flow and the repository's own types are
 * the whole question, and a user who opened the wizard by hand is the one
 * most likely to want the answer. The store states the absence in the request
 * so the reason comes back as a convention rather than as a reading.
 */
const accountTypeAskable = computed(
  () => isCreating.value && !accountTypeSuggestionStore.hasAsked,
)

const handleAskAccountSuggestion = () => {
  accountSuggestionStore.suggest({
    sentence: sentence!,
    flow,
    transactionType,
    accounts: accounts.value,
  })
}

const handleAskAccountTypeSuggestion = () => {
  accountTypeSuggestionStore.suggest({
    sentence,
    flow,
    transactionType,
    accountTypes: accountTypes.value,
  })
}
</script>
